/**
 * businessInsightsSalesData.ts — Tarjetas demo de ventas y demanda.
 *
 * QUÉ HACE: declara las tarjetas del explorador de Análisis del negocio del
 *   grupo ventas y demanda (ticket promedio por día, ventas por categoría,
 *   pronóstico de ventas, meta vs real y acumulado del mes —con comparación
 *   contra el plan y el mes anterior—). Son datos DEMO deterministas (sin
 *   `Math.random` y sin storage) para la pestaña de pruebas del entorno local.
 *
 * QUÉ INVOCA: helpers de `businessInsights.shared.ts`.
 * QUÉ EXPORTA: BUSINESS_INSIGHTS_SALES (readonly BusinessInsightDataset[]).
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 */
import { PieChart, Receipt, Sparkles, Target, TrendingUp } from "lucide-react";
import type { BusinessInsightDataset } from "../types/businessInsights.types";
import {
  INSIGHT_COLORS,
  INSIGHT_VIEW_PRESETS,
  insightCandleRow,
  insightRow,
} from "./businessInsights.shared";

/* ── Ventas por día del mes / acumulado ──────────────────────────────────── */

const MONTH_DAILY = [
  8400000, 10100000, 9600000, 11200000, 8800000, 12300000, 10400000, 9900000,
  11700000, 13100000, 10600000, 21300000, 9800000, 11400000, 12800000, 10900000,
  12100000, 9300000, 11800000, 13500000, 10200000, 12600000, 14100000, 10800000,
  11600000, 12400000, 9700000, 11200000, 12900000, 10400000,
];

const MONTH_PLAN_TOTAL = 350000000;

const MONTH_ACCUMULATED_ROWS = (() => {
  let acumulado = 0;
  return MONTH_DAILY.map((value, index) => {
    acumulado += value;
    return insightRow(String(index + 1), {
      acumulado,
      plan: Math.round((MONTH_PLAN_TOTAL * (index + 1)) / MONTH_DAILY.length),
      anterior: Math.round(acumulado * 0.86),
    });
  });
})();

/* ── Tarjetas ────────────────────────────────────────────────────────────── */

export const BUSINESS_INSIGHTS_SALES: readonly BusinessInsightDataset[] = [
  {
    id: "ticket-promedio-dia",
    title: "Ticket promedio por día",
    subtitle: "Promedio por venta — últimos 7 días",
    icon: Receipt,
    group: "clientes",
    valueKind: "currency",
    nameLabel: "Día",
    series: [
      { key: "ticket", label: "Ticket promedio", color: INSIGHT_COLORS.caja },
    ],
    rows: [
      insightCandleRow("Lun", { ticket: 42000 }, "ticket"),
      insightCandleRow("Mar", { ticket: 48000 }, "ticket"),
      insightCandleRow("Mié", { ticket: 39000 }, "ticket"),
      insightCandleRow("Jue", { ticket: 51000 }, "ticket"),
      insightCandleRow("Vie", { ticket: 63000 }, "ticket"),
      insightCandleRow("Sáb", { ticket: 71000 }, "ticket"),
      insightCandleRow("Dom", { ticket: 55000 }, "ticket"),
    ],
    views: [...INSIGHT_VIEW_PRESETS.temporal, "radar"],
    badge: { mode: "average", label: "Promedio", tone: "blue" },
  },
  {
    id: "ventas-categoria",
    title: "Ventas por categoría",
    subtitle: "Participación de ingresos — último mes",
    icon: PieChart,
    group: "ventas",
    valueKind: "currency",
    nameLabel: "Categoría",
    series: [{ key: "ventas", label: "Ventas", color: INSIGHT_COLORS.caja }],
    rows: [
      insightRow("Electrodomésticos", { ventas: 15200000 }),
      insightRow("Tecnología", { ventas: 9500000 }),
      insightRow("Hogar", { ventas: 6800000 }),
      insightRow("Accesorios", { ventas: 4200000 }),
      insightRow("Otros", { ventas: 2300000 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "max", label: "Top", tone: "green" },
  },
  {
    id: "pronostico-ventas",
    title: "Pronóstico de ventas",
    subtitle: "Real vs proyectado — próximos 7 días",
    icon: Sparkles,
    group: "ventas",
    valueKind: "currency",
    nameLabel: "Día",
    series: [
      { key: "real", label: "Real", color: INSIGHT_COLORS.caja },
      { key: "proyectado", label: "Proyectado", color: INSIGHT_COLORS.alerta },
    ],
    rows: [
      insightRow("Hace 3 d", { real: 16800000, proyectado: 0 }),
      insightRow("Hace 2 d", { real: 15200000, proyectado: 0 }),
      insightRow("Hace 1 d", { real: 17400000, proyectado: 0 }),
      insightRow("Hoy", { real: 18100000, proyectado: 18100000 }),
      insightRow("Mañana", { real: 0, proyectado: 19500000 }),
      insightRow("+2 d", { real: 0, proyectado: 20800000 }),
      insightRow("+3 d", { real: 0, proyectado: 18900000 }),
      insightRow("+4 d", { real: 0, proyectado: 22400000 }),
      insightRow("+5 d", { real: 0, proyectado: 23100000 }),
      insightRow("+6 d", { real: 0, proyectado: 19700000 }),
    ],
    views: ["banda", ...INSIGHT_VIEW_PRESETS.temporalSinVelas, "radar"],
    defaultView: "banda",
    band: { midKey: "proyectado", spreadPct: 12 },
    badge: {
      mode: "max",
      label: "Día más alto",
      tone: "amber",
      seriesKey: "proyectado",
    },
    sliceKey: "proyectado",
    showTotal: false,
  },
  {
    id: "meta-vs-real",
    title: "Meta vs real",
    subtitle: "Ventas vs meta — últimos 6 meses",
    icon: Target,
    group: "ventas",
    valueKind: "currency",
    nameLabel: "Mes",
    series: [
      { key: "real", label: "Real", color: INSIGHT_COLORS.exito },
      { key: "meta", label: "Meta", color: INSIGHT_COLORS.neutro },
    ],
    rows: ["Abr", "May", "Jun", "Jul", "Ago", "Sep"].map((month, index) =>
      insightRow(month, {
        real: [214, 226, 238, 251, 268, 292][index] * 1000000,
        meta: [240, 240, 250, 260, 280, 300][index] * 1000000,
      }),
    ),
    views: ["progreso", ...INSIGHT_VIEW_PRESETS.temporal, "radar", "gauge"],
    defaultView: "progreso",
    progress: {
      valueKey: "real",
      targetKey: "meta",
      targetLabel: "Meta",
      summary: "sum",
    },
    badge: {
      mode: "max",
      label: "Mejor mes",
      tone: "green",
      seriesKey: "real",
    },
  },
  {
    id: "acumulado-mes",
    title: "Acumulado del mes",
    subtitle: "Real vs plan vs mes anterior — cierre estimado $410M",
    icon: TrendingUp,
    group: "ventas",
    valueKind: "currency",
    nameLabel: "Día",
    series: [
      { key: "acumulado", label: "Acumulado", color: INSIGHT_COLORS.caja },
      { key: "plan", label: "Plan", color: INSIGHT_COLORS.neutro },
      { key: "anterior", label: "Mes anterior", color: INSIGHT_COLORS.violeta },
    ],
    rows: MONTH_ACCUMULATED_ROWS,
    views: [
      "line",
      "progreso",
      "gauge",
      "bar",
      "matriz",
      "donut",
      "treemap",
      "barHorizontal",
      "tabla",
      "velas",
    ],
    defaultView: "line",
    progress: {
      valueKey: "acumulado",
      targetKey: "plan",
      targetLabel: "Plan",
      summary: "last",
    },
    badge: {
      mode: "max",
      label: "Acumulado",
      tone: "blue",
      seriesKey: "acumulado",
    },
  },
];
